import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Building2, MapPin, Plus, Users } from "lucide-react";
import type { SiteSummary } from "@crewledger/shared/schemas";
import { EmptyState, PageHeader } from "@/components/design";
import { Button } from "@/components/ui/button";
import { BETA_EMPTY_STATES } from "@/lib/beta-empty-states";
import { fetchSites } from "./api";
import { SiteStatusBadge } from "./components/site-status-badge";
import { useSitePermissions } from "./use-site-permissions";

export function SitesListPage() {
  const navigate = useNavigate();
  const { canWrite } = useSitePermissions();
  const [sites, setSites] = useState<SiteSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [includeArchived, setIncludeArchived] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetchSites({ includeArchived, pageSize: 50 })
      .then((res) => { setSites(res.data); })
      .catch((err: unknown) =>
        { setError(err instanceof Error ? err.message : "Failed to load sites"); },
      )
      .finally(() => { setLoading(false); });
  }, [includeArchived]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Sites & Jobs"
        description="Manage construction sites, job codes, and site managers."
        actions={
          canWrite ? (
            <Link to="/sites/new">
              <Button>
                <Plus className="mr-1.5 h-4 w-4" />
                New site
              </Button>
            </Link>
          ) : undefined
        }
      />

      <div className="flex items-center gap-2">
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={(e) => { setIncludeArchived(e.target.checked); }}
            className="rounded border-input"
          />
          Show archived
        </label>
      </div>

      {error ? (
        <EmptyState
          icon={Building2}
          title="Could not load sites"
          description={error}
          actionLabel="Retry"
          onAction={() => { window.location.reload(); }}
        />
      ) : loading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-44 animate-pulse rounded-xl border bg-card-white" />
          ))}
        </div>
      ) : sites.length === 0 ? (
        <EmptyState
          icon={Building2}
          title={BETA_EMPTY_STATES.sites.title}
          description={BETA_EMPTY_STATES.sites.description}
          actionLabel={canWrite ? BETA_EMPTY_STATES.sites.actionLabel : undefined}
          onAction={canWrite ? () => { navigate("/sites/new"); } : undefined}
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {sites.map((site) => (
            <Link
              key={site.id}
              to={`/sites/${site.id}`}
              className="group rounded-xl border bg-card-white p-5 shadow-[var(--shadow-card)] transition-shadow hover:shadow-[var(--shadow-elevated)]"
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-mono text-xs font-bold uppercase tracking-widest text-orange-construction">
                    {site.code}
                  </p>
                  <h3 className="mt-1 font-semibold text-navy-deep group-hover:text-orange-construction">
                    {site.name}
                  </h3>
                </div>
                <SiteStatusBadge status={site.status} />
              </div>

              {site.address ? (
                <p className="mt-2 flex items-start gap-1.5 text-sm text-muted-foreground">
                  <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  {site.address}
                </p>
              ) : null}

              <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                {site.defaultPayRuleProfileName ? (
                  <span>{site.defaultPayRuleProfileName}</span>
                ) : null}
                {site.managers.length > 0 ? (
                  <span className="flex items-center gap-1">
                    <Users className="h-3.5 w-3.5" />
                    {site.managers.length} manager{site.managers.length === 1 ? "" : "s"}
                  </span>
                ) : null}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
